import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle, ArrowLeft, ChevronDown, Download, Loader2, RefreshCw, Send, Sparkles, TextQuote,
} from 'lucide-react';
import { SiFacebook, SiInstagram, SiTiktok } from 'react-icons/si';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ProFeatureDialog } from '@/components/ProFeatureDialog';
import { SlideList } from '@/components/quote-carousel/SlideList';
import { TranscriptSpanPicker } from '@/components/quote-carousel/TranscriptSpanPicker';
import { CarouselSettingsPanel } from '@/components/quote-carousel/CarouselSettingsPanel';
import { SlidePreview } from '@/components/quote-carousel/SlidePreview';
import { SlideFilmstrip } from '@/components/quote-carousel/SlideFilmstrip';
import { ExportProgressDialog } from '@/components/quote-carousel/ExportProgressDialog';
import { PublishCarouselDialog } from '@/components/quote-carousel/PublishCarouselDialog';
import { GET_MY_BRAND_KITS, GET_MY_CLIP } from '@/queries/queries';
import type {
  MyBrandKitsQuery, MyClipQuery, MyClipQueryVariables,
} from '@/generated/graphql';
import { useHasFeature } from '@/lib/useHasFeature';
import { useSocialPostingPlatforms } from '@/hooks/use-social-posting-platforms';
import { SOCIAL_PUBLISHING_FEATURE } from '@/lib/publishing/socialPublishingFeature';
import { useQuoteSuggestions, type QuoteSuggestionState } from '@/hooks/use-quote-suggestions';
import { useAutoSaveQuoteCarousel } from '@/hooks/use-auto-save-quote-carousel';
import { useCarouselFrameCapture } from '@/hooks/use-carousel-frame-capture';
import { useCarouselExport } from '@/hooks/use-carousel-export';
import { useSlideKeyboardNavigation } from '@/hooks/use-slide-keyboard-navigation';
import { emptyQuoteCarousel, sanitizeQuoteCarousel } from '@/lib/quoteCarousel/sanitizeQuoteCarousel';
import { selectTranscriptWords } from '@/lib/quoteCarousel/selectTranscriptWords';
import { buildSlideFromQuote } from '@/lib/quoteCarousel/buildSlideFromQuote';
import { brandFromKit, buildQuoteSlideProps } from '@/lib/quoteCarousel/buildQuoteSlideProps';
import {
  MAX_SLIDES, MIN_SLIDES, type QuoteCarousel, type QuoteSlide,
} from '@/lib/quoteCarousel/types';
import {
  clipCaptureIntervals, resolveClipSourceParent, resolveOriginalFrameSource, slidesNeedingFrameCapture,
} from '@/lib/quoteCarousel/frameCaptureTargets';
import {
  QUOTE_CAROUSEL_LIBRARY_SOURCE,
  QUOTE_CAROUSEL_PROJECT_SOURCE,
  type QuoteCarouselLibrarySourceData,
  type QuoteCarouselProjectSourceData,
  type QuoteCarouselSourceVars,
} from '@/queries/quoteCarousel';
import { resolveEffectiveFrameSource } from '@/lib/quoteCarousel/resolveEffectiveFrameSource';
import {
  resolveAutoFrameTime, resolveEditedFrameSource, type FrameCaptureSources,
} from '@/lib/quoteCarousel/resolveFrameCapturePlan';
import { validateCarouselForExport } from '@/lib/quoteCarousel/validateCarouselForExport';
import { resetSlidePlacement } from '@/lib/quoteCarousel/slidePlacement';
import { resolveQuoteTextStyle } from '@/lib/quoteCarousel/quoteTextStyle';
import { buildCarouselZip } from '@/lib/quoteCarousel/buildCarouselZip';
import { carouselZipName, slideFileName } from '@/lib/quoteCarousel/exportFileNames';
import { resolveSlideDownloadGate } from '@/lib/quoteCarousel/resolveSlideDownloadGate';
import { frameDataUrlToFile } from '@/lib/media/frameCapture';
import { resolveCarouselCaption } from '@/lib/quoteCarousel/resolveCarouselCaption';
import type { CarouselPublishPlatform } from '@/lib/quoteCarousel/buildCarouselPublishInput';

/** Reason the ZIP download was refused before rendering. */
type ExportBlockReason = 'too_few_slides' | 'empty_text' | 'frames_pending';

/** Saves a blob through a temporary `<a download>`. */
function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Auto-generation on first open needs at least this many transcript words. */
const AUTO_GENERATE_MIN_WORDS = 5;

const makeSlideId = () => crypto.randomUUID();

const QuoteCarouselPage: React.FC = () => {
  const { clipId } = useParams<{ clipId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isPro = useHasFeature('pro_features');
  const hasSocialPublishing = useHasFeature(SOCIAL_PUBLISHING_FEATURE);
  const postingPlatforms = useSocialPostingPlatforms();

  const {
    data: clipData, loading: clipLoading, error: clipError, refetch: refetchClip,
  } = useQuery<MyClipQuery, MyClipQueryVariables>(GET_MY_CLIP, {
    variables: { id: clipId ?? '' },
    skip: !clipId,
    fetchPolicy: 'network-only',
  });
  const { data: kitsData } = useQuery<MyBrandKitsQuery>(GET_MY_BRAND_KITS);

  const clip = clipData?.myClip ?? null;
  const clipDataJson = clip?.data as Record<string, unknown> | null | undefined;

  // "Vídeo original" = the parent project's/library's FULL source video
  // (source axis, like the transcript and segments) — never the clip's cut.
  const sourceParent = resolveClipSourceParent(clip);
  const projectSource = useQuery<QuoteCarouselProjectSourceData, QuoteCarouselSourceVars>(
    QUOTE_CAROUSEL_PROJECT_SOURCE,
    { variables: { id: sourceParent?.id ?? '' }, skip: sourceParent?.kind !== 'project' },
  );
  const librarySource = useQuery<QuoteCarouselLibrarySourceData, QuoteCarouselSourceVars>(
    QUOTE_CAROUSEL_LIBRARY_SOURCE,
    { variables: { id: sourceParent?.id ?? '' }, skip: sourceParent?.kind !== 'library' },
  );
  const parentSource = sourceParent?.kind === 'library'
    ? librarySource.data?.myLibrary
    : projectSource.data?.myProject;
  const activeSourceQuery = sourceParent?.kind === 'library' ? librarySource : projectSource;
  // Settled = answered (or failed, or no parent at all): until then a pending
  // original frame must wait, not fall back to color.
  const sourceSettled = !sourceParent
    || activeSourceQuery.data !== undefined
    || activeSourceQuery.error !== undefined;
  const originalFrameUrl = resolveOriginalFrameSource(parentSource);
  let originalStatus: 'ready' | 'loading' | 'error' = 'ready';
  if (!originalFrameUrl && activeSourceQuery.error) originalStatus = 'error';
  else if (!originalFrameUrl && !sourceSettled) originalStatus = 'loading';
  const editedFrameUrl = clip ? resolveEditedFrameSource(clip) : null;
  const frameSources = useMemo<FrameCaptureSources>(
    () => ({ original: originalFrameUrl, edited: editedFrameUrl }),
    [editedFrameUrl, originalFrameUrl],
  );

  const [carousel, setCarousel] = useState<QuoteCarousel | null>(null);
  // Tag: the clip the CURRENT `carousel` belongs to (editor pattern).
  const [loadedClipId, setLoadedClipId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false);
  const [proDialogOpen, setProDialogOpen] = useState(false);
  const [notEnoughQuotes, setNotEnoughQuotes] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportBlock, setExportBlock] = useState<ExportBlockReason | null>(null);
  // Which action the refusal notice refers to (its copy differs).
  const [blockedAction, setBlockedAction] = useState<'export' | 'publish'>('export');
  const [stills, setStills] = useState<Array<string | null>>([]);
  const [zipping, setZipping] = useState(false);
  const [zipError, setZipError] = useState(false);
  const [publishPlatform, setPublishPlatform] = useState<CarouselPublishPlatform | null>(null);
  const [publishProDialogOpen, setPublishProDialogOpen] = useState(false);
  // "Baixar imagem": the slide being downloaded (waiting for its frame, then
  // rendering) and whether the last single download failed.
  const [singleDownload, setSingleDownload] = useState<{ slideId: string; status: 'waiting' | 'rendering' } | null>(null);
  const [singleDownloadError, setSingleDownloadError] = useState(false);

  // Seed the carousel once per loaded clip.
  useEffect(() => {
    if (!clip || clip.id !== clipId || loadedClipId === clip.id) return;
    const loaded = sanitizeQuoteCarousel(clipDataJson?.quoteCarousel) ?? emptyQuoteCarousel();
    setCarousel(loaded);
    setSelectedId(loaded.slides[0]?.id ?? null);
    setNotEnoughQuotes(false);
    setLoadedClipId(clip.id);
  }, [clip, clipDataJson, clipId, loadedClipId]);

  const { saving, flush } = useAutoSaveQuoteCarousel({
    clipId: clipId ?? null,
    carousel,
    loadedClipId,
    enabled: clip?.can_edit !== false,
  });

  const words = useMemo(
    () => selectTranscriptWords(clipDataJson?.words, clipDataJson?.segments),
    [clipDataJson],
  );

  const {
    state: suggestionState, quota, generate,
  } = useQuoteSuggestions(clipId ?? null);

  const isReady = !!carousel && loadedClipId === clipId;

  // First open with no slides: suggest quotes once.
  const autoGenerateRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isReady || !carousel || !clipId) return;
    if (autoGenerateRef.current === clipId) return;
    autoGenerateRef.current = clipId;
    if (carousel.slides.length === 0 && words.length >= AUTO_GENERATE_MIN_WORDS) {
      void generate();
    }
  }, [carousel, clipId, generate, isReady, words.length]);

  // Apply each finished generation exactly once.
  const handledSuggestionRef = useRef<QuoteSuggestionState | null>(null);
  useEffect(() => {
    if (suggestionState.status !== 'done') return;
    if (handledSuggestionRef.current === suggestionState) return;
    handledSuggestionRef.current = suggestionState;

    if (!carousel) return;
    const slides = suggestionState.quotes
      .slice(0, MAX_SLIDES)
      .map((quote) => buildSlideFromQuote(
        quote,
        carousel.style,
        makeSlideId,
        resolveEffectiveFrameSource(carousel.frameSource, frameSources),
      ));
    const enough = slides.length >= MIN_SLIDES;
    // Too few suggestions never wipe an existing carousel; they only seed an
    // empty one.
    if (enough || carousel.slides.length === 0) {
      setCarousel({ ...carousel, slides });
      setSelectedId(slides[0]?.id ?? null);
    }
    setNotEnoughQuotes(!enough);
    if (!enough) setPickerOpen(true);
  }, [carousel, frameSources, suggestionState]);

  const brandKits = useMemo(() => kitsData?.myBrandKits ?? [], [kitsData]);
  const brand = useMemo(() => {
    const kit = brandKits.find((entry) => entry.id === carousel?.brandKitId) ?? null;
    return brandFromKit(kit as Parameters<typeof brandFromKit>[0]);
  }, [brandKits, carousel?.brandKitId]);
  const colorPresets = useMemo(
    () => [brand.backgroundColor, brand.textColor, brand.accentColor],
    [brand],
  );
  // Template + the carousel's "Texto" overrides: what the slides draw.
  const effectiveBrand = useMemo(
    () => (carousel ? resolveQuoteTextStyle({ brand, style: carousel.style, textStyle: carousel.textStyle }).brand : brand),
    [brand, carousel],
  );

  const captureIntervals = useMemo(
    () => clipCaptureIntervals({
      segments: clipDataJson?.segments,
      clipStartTime: clip?.clip_start_time,
      clipEndTime: clip?.clip_end_time,
    }),
    [clip?.clip_end_time, clip?.clip_start_time, clipDataJson],
  );
  const {
    capturing: capturePending, failed: frameCaptureFailed,
  } = useCarouselFrameCapture({
    carousel,
    setCarousel,
    sources: frameSources,
    intervals: captureIntervals,
    transitions: clipDataJson?.transitions,
    coverImage: clipDataJson?.coverImage,
    cropCenterKeyframes: clipDataJson?.cropCenterKeyframes,
    fallbackColor: effectiveBrand.backgroundColor,
    enabled: isReady && sourceSettled && clip?.can_edit !== false,
  });

  // While the source video URL is still loading, capture is held back (not
  // failed): those frames are pending too, so download/publish wait for them.
  const framesPending = capturePending || (
    isReady && !sourceSettled && clip?.can_edit !== false && slidesNeedingFrameCapture(carousel).length > 0
  );

  const {
    progress: exportProgress, running: exportRunning, renderAll, retry: retrySlide, renderOne,
  } = useCarouselExport();

  const slideProps = useMemo(() => {
    if (!carousel) return [];
    return carousel.slides.map((slide, slideIndex) => ({
      id: slide.id,
      props: buildQuoteSlideProps({
        carousel, slideIndex, brand, isPro,
      }),
    }));
  }, [brand, carousel, isPro]);

  // Publishing is PRO-only, so published slides never carry the watermark.
  const publishSlideProps = useMemo(() => {
    if (!carousel) return [];
    return carousel.slides.map((_, slideIndex) => buildQuoteSlideProps({
      carousel, slideIndex, brand, isPro: true,
    }));
  }, [brand, carousel]);

  const effectiveSelectedId = carousel?.slides.some((slide) => slide.id === selectedId)
    ? selectedId
    : carousel?.slides[0]?.id ?? null;
  const selectedEntry = slideProps.find((entry) => entry.id === effectiveSelectedId) ?? null;
  const selectedSlide = carousel?.slides.find((slide) => slide.id === effectiveSelectedId) ?? null;
  // ←/→ and Home/End walk the slides (never while typing or in a dialog).
  useSlideKeyboardNavigation({
    slideIds: carousel?.slides.map((slide) => slide.id) ?? [],
    selectedId: effectiveSelectedId,
    onSelect: setSelectedId,
    enabled: isReady,
  });
  // Only show "capturing" when a capture will actually run (see framesPending).
  const selectedCapturing = framesPending
    && selectedSlide?.background?.kind === 'frame'
    && selectedSlide.background.url === null;

  const running = suggestionState.status === 'running';
  const atQuotaLimit = !!quota && quota.used >= quota.limit;

  const handleGenerateClick = () => {
    if (atQuotaLimit) {
      if (!isPro) {
        setProDialogOpen(true);
        return;
      }
      // PRO at the limit: generate() refuses locally and surfaces
      // `daily_limit_reached` without calling the mutation.
      void generate();
      return;
    }
    if (carousel && carousel.slides.length > 0) {
      setConfirmRegenerateOpen(true);
      return;
    }
    void generate();
  };

  const handleAddFromTranscript = (quote: { text: string; start: number; end: number }) => {
    if (!carousel || carousel.slides.length >= MAX_SLIDES) return;
    const slide = buildSlideFromQuote(
        quote,
        carousel.style,
        makeSlideId,
        resolveEffectiveFrameSource(carousel.frameSource, frameSources),
      );
    const slides = [...carousel.slides, slide];
    setCarousel({ ...carousel, slides });
    setSelectedId(slide.id);
    if (slides.length >= MIN_SLIDES) setNotEnoughQuotes(false);
    setPickerOpen(false);
  };

  // Any edit (or a finished frame capture) invalidates the refusal notice.
  useEffect(() => {
    setExportBlock(null);
  }, [carousel]);

  const saveZip = async (dataUrls: Array<string | null>) => {
    setZipping(true);
    setZipError(false);
    try {
      const blob = await buildCarouselZip(dataUrls, 'png');
      downloadBlob(blob, carouselZipName(clip?.name ?? null));
      return true;
    } catch {
      setZipError(true);
      return false;
    } finally {
      setZipping(false);
    }
  };

  const handleDownloadClick = async () => {
    if (!carousel || exportRunning || zipping) return;
    setBlockedAction('export');
    const validation = validateCarouselForExport(carousel);
    if (validation.ok === false) {
      setExportBlock(validation.reason);
      return;
    }
    if (framesPending) {
      setExportBlock('frames_pending');
      return;
    }
    setExportBlock(null);
    setZipError(false);
    flush();
    setExportOpen(true);
    // Free users get the watermark: `buildQuoteSlideProps` already resolved it.
    const rendered = await renderAll(slideProps.map((entry) => entry.props), 'png');
    setStills(rendered);
    if (rendered.every((url) => url !== null)) {
      if (await saveZip(rendered)) setExportOpen(false);
    }
  };

  const handlePublishClick = (platform: CarouselPublishPlatform) => {
    if (!carousel) return;
    if (!isPro) {
      setPublishProDialogOpen(true);
      return;
    }
    setBlockedAction('publish');
    const validation = validateCarouselForExport(carousel);
    if (validation.ok === false) {
      setExportBlock(validation.reason);
      return;
    }
    if (framesPending) {
      setExportBlock('frames_pending');
      return;
    }
    setExportBlock(null);
    setPublishPlatform(platform);
  };

  const updateSlide = (id: string, update: (slide: QuoteSlide) => QuoteSlide) => {
    setCarousel((prev) => (prev
      ? { ...prev, slides: prev.slides.map((slide) => (slide.id === id ? update(slide) : slide)) }
      : prev));
  };

  const handleRetrySlide = async (index: number) => {
    const url = await retrySlide(index);
    if (url) setStills((prev) => prev.map((entry, i) => (i === index ? url : entry)));
  };

  const handleDownloadImage = (slideId: string) => {
    if (!carousel || singleDownload) return;
    const slide = carousel.slides.find((entry) => entry.id === slideId);
    if (!slide) return;
    setSingleDownloadError(false);
    // Same text gate as the ZIP (the slide-count rule is carousel-only).
    if (resolveSlideDownloadGate({ slide, capturePending: framesPending }) === 'empty_text') {
      setBlockedAction('export');
      setExportBlock('empty_text');
      return;
    }
    setExportBlock(null);
    flush();
    setSingleDownload({ slideId, status: 'waiting' });
  };

  // Runs a requested single download once its slide's frame is captured
  // (or will not be), with the same props — and watermark — as the ZIP.
  useEffect(() => {
    if (!singleDownload || singleDownload.status !== 'waiting' || !carousel) return;
    const index = carousel.slides.findIndex((entry) => entry.id === singleDownload.slideId);
    const entry = slideProps[index];
    if (index < 0 || !entry) {
      setSingleDownload(null);
      return;
    }
    const gate = resolveSlideDownloadGate({ slide: carousel.slides[index], capturePending: framesPending });
    if (gate === 'awaiting_frame') return;
    if (gate === 'empty_text') {
      setSingleDownload(null);
      return;
    }
    setSingleDownload({ slideId: singleDownload.slideId, status: 'rendering' });
    void renderOne(entry.props, 'png').then((dataUrl) => {
      let saved = false;
      if (dataUrl) {
        try {
          // A Blob/object URL, not a multi-MB data URL in <a href>.
          downloadBlob(frameDataUrlToFile(dataUrl, 0), slideFileName(index, 'png'));
          saved = true;
        } catch {
          saved = false;
        }
      }
      if (!saved) setSingleDownloadError(true);
      setSingleDownload(null);
    });
  }, [carousel, framesPending, renderOne, singleDownload, slideProps]);

  const renderBody = () => {
    if (!clipId) return null;

    if (clipLoading && !clip) {
      return (
        <div className="flex items-center justify-center py-24" data-testid="quote-carousel-loading">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (clipError || !clip) {
      return (
        <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-24 text-center" data-testid="quote-carousel-error">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm text-muted-foreground">
            {clipError ? t('quoteCarousel.loadError') : t('quoteCarousel.notFound')}
          </p>
          <div className="flex gap-2">
            {clipError && (
              <Button variant="outline" onClick={() => void refetchClip()}>
                <RefreshCw className="mr-1 h-4 w-4" />
                {t('quoteCarousel.retry')}
              </Button>
            )}
            <Button variant="secondary" onClick={() => navigate('/clips')}>
              {t('quoteCarousel.backToClips')}
            </Button>
          </div>
        </div>
      );
    }

    if (!isReady || !carousel) {
      return (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    return (
      <div className="grid gap-6 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)_minmax(0,280px)]">
        {/* Slides column */}
        <section className="flex flex-col gap-3" aria-label={t('quoteCarousel.slides.title')}>
          <div className="flex flex-col gap-1">
            <Button
              data-testid="quote-generate"
              variant={carousel.slides.length > 0 ? 'outline' : 'default'}
              disabled={running || words.length === 0}
              onClick={handleGenerateClick}
            >
              {running ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-1 h-4 w-4" />
              )}
              {carousel.slides.length > 0 ? t('quoteCarousel.regenerate') : t('quoteCarousel.generate')}
            </Button>
            {quota && (
              <p className="text-center text-xs text-muted-foreground" data-testid="quote-quota">
                {t('quoteCarousel.quota', { used: quota.used, limit: quota.limit })}
              </p>
            )}
          </div>

          {words.length === 0 && (
            <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground" data-testid="quote-no-transcript">
              {t('quoteCarousel.noTranscript')}
            </p>
          )}

          {suggestionState.status === 'error' && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive" data-testid="quote-generate-error">
              {t(`quoteCarousel.errors.${suggestionState.errorKey}`)}
            </p>
          )}

          {notEnoughQuotes && (
            <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground" data-testid="quote-not-enough">
              {t('quoteCarousel.notEnoughQuotes', { min: MIN_SLIDES })}
            </p>
          )}

          {running ? (
            <div className="flex flex-col gap-3" data-testid="quote-slides-skeleton">
              <p className="text-xs text-muted-foreground">{t('quoteCarousel.generating')}</p>
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-28 w-full" />
              ))}
            </div>
          ) : (
            <>
              {carousel.slides.length === 0 && (
                <div className="flex flex-col items-center gap-2 rounded-md border border-dashed p-6 text-center">
                  <TextQuote className="h-6 w-6 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">{t('quoteCarousel.empty')}</p>
                </div>
              )}
              <SlideList
                slides={carousel.slides}
                selectedId={effectiveSelectedId}
                onSelect={setSelectedId}
                onChange={(slides) => setCarousel({ ...carousel, slides })}
                onAddFromTranscript={() => setPickerOpen(true)}
              />
            </>
          )}
        </section>

        {/* Preview column */}
        <section className="flex min-w-0 flex-col gap-4" aria-label={t('quoteCarousel.preview.title')}>
          {selectedEntry ? (
            <SlidePreview
              key={selectedEntry.id}
              props={selectedEntry.props}
              capturing={selectedCapturing}
              onPlacementChange={(patch) => updateSlide(selectedEntry.id, (slide) => ({ ...slide, ...patch }))}
              onResetPlacement={() => updateSlide(selectedEntry.id, resetSlidePlacement)}
              onDownloadImage={() => handleDownloadImage(selectedEntry.id)}
              downloadingImage={singleDownload?.slideId === selectedEntry.id}
            />
          ) : (
            <div className="mx-auto flex aspect-[4/5] w-full max-w-[420px] items-center justify-center rounded-lg border border-dashed text-center text-xs text-muted-foreground">
              {t('quoteCarousel.preview.empty')}
            </div>
          )}
          {singleDownloadError && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive" data-testid="quote-single-download-error">
              {t('quoteCarousel.singleImage.error')}
            </p>
          )}
          {frameCaptureFailed && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive" data-testid="quote-frame-capture-failed">
              {t('quoteCarousel.frameCaptureFailed')}
            </p>
          )}
          {slideProps.length > 0 && (
            <SlideFilmstrip
              items={slideProps}
              selectedId={effectiveSelectedId}
              onSelect={setSelectedId}
              onDownload={handleDownloadImage}
              downloadingId={singleDownload?.slideId ?? null}
            />
          )}
        </section>

        {/* Settings column */}
        <aside className="rounded-lg border bg-card p-4" aria-label={t('quoteCarousel.settings.title')}>
          <CarouselSettingsPanel
            carousel={carousel}
            onChange={setCarousel}
            brandKits={brandKits}
            selectedSlideId={effectiveSelectedId}
            frameSources={frameSources}
            autoFrameTime={(slide, source) => resolveAutoFrameTime({
              source,
              slide,
              intervals: captureIntervals,
              transitions: clipDataJson?.transitions,
              coverImage: clipDataJson?.coverImage,
            })}
            defaultBackgroundColor={effectiveBrand.backgroundColor}
            colorPresets={colorPresets}
            templateBrand={brand}
            originalStatus={originalStatus}
            onRetryOriginal={() => {
              void activeSourceQuery.refetch().catch(() => undefined);
            }}
          />
        </aside>
      </div>
    );
  };

  return (
    <Layout>
      <div className="mx-auto w-full max-w-7xl px-4 py-6">
        <header className="mb-6 flex flex-wrap items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('quoteCarousel.back')}
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-xl font-semibold">{t('quoteCarousel.title')}</h1>
            {clip?.name && (
              <p className="truncate text-sm text-muted-foreground">{clip.name}</p>
            )}
          </div>
          {isReady && (
            <span className="text-xs text-muted-foreground" data-testid="quote-carousel-save-status">
              {saving ? t('quoteCarousel.saving') : t('quoteCarousel.saved')}
            </span>
          )}
          {/* Top-bar actions: "Baixar ZIP" and "Postar" (Task F7). */}
          <div className="flex items-center gap-2" data-testid="quote-carousel-actions">
            {isReady && (
              <Button
                variant="outline"
                data-testid="quote-download-zip"
                disabled={exportRunning || zipping}
                onClick={() => void handleDownloadClick()}
              >
                {exportRunning || zipping ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-1 h-4 w-4" />
                )}
                {t('quoteCarousel.export.download')}
              </Button>
            )}
            {isReady && hasSocialPublishing && (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="quote-publish-menu" disabled={exportRunning || zipping}>
                    <Send className="mr-1 h-4 w-4" />
                    {t('quoteCarousel.publish.menu')}
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    data-testid="quote-publish-instagram"
                    onSelect={() => handlePublishClick('instagram')}
                  >
                    <SiInstagram className="mr-2 h-4 w-4" aria-hidden="true" />
                    Instagram
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="quote-publish-tiktok"
                    onSelect={() => handlePublishClick('tiktok')}
                  >
                    <SiTiktok className="mr-2 h-4 w-4" aria-hidden="true" />
                    TikTok
                  </DropdownMenuItem>
                  {postingPlatforms.includes('facebook') && (
                    <DropdownMenuItem
                      data-testid="quote-publish-facebook"
                      onSelect={() => handlePublishClick('facebook')}
                    >
                      <SiFacebook className="mr-2 h-4 w-4" aria-hidden="true" />
                      Facebook
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </header>

        {exportBlock && (
          <p
            className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive"
            data-testid="quote-export-blocked"
          >
            {t(`quoteCarousel.${blockedAction}.blocked.${exportBlock}`, { min: MIN_SLIDES })}
          </p>
        )}

        {renderBody()}
      </div>

      <TranscriptSpanPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        words={words}
        onAdd={handleAddFromTranscript}
      />

      <Dialog open={confirmRegenerateOpen} onOpenChange={setConfirmRegenerateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="quote-regenerate-dialog">
          <DialogHeader>
            <DialogTitle>{t('quoteCarousel.regenerateConfirm.title')}</DialogTitle>
            <DialogDescription>{t('quoteCarousel.regenerateConfirm.body')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="secondary" onClick={() => setConfirmRegenerateOpen(false)}>
              {t('quoteCarousel.regenerateConfirm.cancel')}
            </Button>
            <Button
              data-testid="quote-regenerate-confirm"
              onClick={() => {
                setConfirmRegenerateOpen(false);
                void generate();
              }}
            >
              {t('quoteCarousel.regenerateConfirm.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExportProgressDialog
        open={exportOpen}
        progress={exportProgress}
        running={exportRunning}
        downloading={zipping}
        errorMessage={zipError ? t('quoteCarousel.export.zipError') : null}
        onRetry={(index) => void handleRetrySlide(index)}
        onDownload={() => {
          void saveZip(stills).then((ok) => {
            if (ok) setExportOpen(false);
          });
        }}
        onClose={() => setExportOpen(false)}
      />

      {clipId && publishPlatform && carousel && (
        <PublishCarouselDialog
          open
          onOpenChange={(next) => {
            if (!next) setPublishPlatform(null);
          }}
          platform={publishPlatform}
          clipId={clipId}
          clipName={clip?.name ?? ''}
          initialCaption={resolveCarouselCaption({
            copyResult: clip?.copy?.result,
            slides: carousel.slides,
          })}
          slideProps={publishSlideProps}
          format={carousel.format}
          // Existing frame backgrounds are cover-fit by QuoteSlide, so a
          // format switch needs no recapture — same as the settings panel.
          onFormatChange={(format) => setCarousel((prev) => (prev ? { ...prev, format } : prev))}
          onBeforePublish={flush}
        />
      )}

      <ProFeatureDialog
        open={publishProDialogOpen}
        onOpenChange={setPublishProDialogOpen}
        onUpgrade={() => {
          setPublishProDialogOpen(false);
          navigate('/plans');
        }}
        title={t('quoteCarousel.publish.proDialog.title')}
        body={t('quoteCarousel.publish.proDialog.body')}
        dismissLabel={t('quoteCarousel.proDialog.dismiss')}
        upgradeLabel={t('quoteCarousel.proDialog.upgrade')}
        testIdPrefix="quote-carousel-publish-pro"
      />

      <ProFeatureDialog
        open={proDialogOpen}
        onOpenChange={setProDialogOpen}
        onUpgrade={() => {
          setProDialogOpen(false);
          navigate('/plans');
        }}
        title={t('quoteCarousel.proDialog.title')}
        body={t('quoteCarousel.proDialog.body')}
        dismissLabel={t('quoteCarousel.proDialog.dismiss')}
        upgradeLabel={t('quoteCarousel.proDialog.upgrade')}
        testIdPrefix="quote-carousel-pro"
      />
    </Layout>
  );
};

export default QuoteCarouselPage;
